import React from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Grid,
  Link,
  Typography as MuiTypography,
  List,
  ListItem,
} from "@mui/material";
import { spacing } from "@mui/system";

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Typography = styled(MuiTypography)(spacing);

function PoliciesAndProcedures() {
  return (
    <React.Fragment>
      <Helmet title="Policies and Procedures" />

      <Grid container spacing={6} justifyContent="center">
        <Grid item xs={12} lg={9} xl={7}>
          <Typography variant="h2" gutterBottom display="inline">
            Documentation and Record Keeping Policy
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Typography>Documentation and Record Keeping Policy</Typography>
          </Breadcrumbs>

          <Divider my={6} />

          <Typography variant="body1" paragraph>
            This section of the documentation delineates the essential policies and procedures for effective
            documentation and record-keeping in alignment with the Health Insurance Portability and Accountability Act
            (HIPAA) requirements. Proper documentation and record-keeping are crucial for demonstrating compliance with
            HIPAA standards, facilitating audits, and ensuring the privacy and security of Protected Health Information
            (PHI). It encompasses the processes for creating, maintaining, and securing documentation related to HIPAA
            compliance efforts, employee training, incident response, and PHI management.
          </Typography>

          <Typography variant="h5" gutterBottom>
            Documentation and Record Keeping Overview
          </Typography>
          <List>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Compliance Documentation:</b> This category includes all documentation that supports our HIPAA
                  compliance efforts. It covers policies and procedures, privacy and security measures, training
                  records, and any changes or updates to our practices. Compliance documentation is critical for
                  demonstrating our adherence to HIPAA regulations during audits and reviews.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Retention of Records:</b> Policies detailing the duration for which various types of documentation
                  and PHI must be retained, in compliance with HIPAA requirements. This section also outlines the secure
                  storage, access, and destruction policies for outdated or no longer needed records, ensuring that PHI
                  is protected throughout its lifecycle.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Incident Documentation and Reporting:</b> Procedures for documenting and reporting any security
                  incidents or breaches, including the mechanisms for internal reporting, analysis, and notification to
                  affected individuals and regulatory bodies as required. This ensures timely and effective responses to
                  incidents impacting the privacy and security of PHI.
                </Typography>
              </Box>
            </ListItem>
          </List>

          <Typography variant="h5" gutterBottom>
            Objectives
          </Typography>
          <List>
            <ListItem>
              To ensure comprehensive and accessible documentation that supports our HIPAA compliance, facilitates
              audits, and serves as a reference for our privacy and security practices.
            </ListItem>
            <ListItem>
              To establish robust procedures for the retention, protection, and disposal of PHI and compliance-related
              documentation, safeguarding the information throughout its lifecycle.
            </ListItem>
            <ListItem>
              To create a clear and efficient process for documenting and responding to security incidents, reinforcing
              our commitment to maintaining the confidentiality, integrity, and availability of PHI.
            </ListItem>
          </List>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default PoliciesAndProcedures;
